import BaseLayer from 'ol/layer/Base';
import { DecimalPipe } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { scaleLinear } from 'd3-scale';
import GeoJSON from 'ol/format/GeoJSON';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Fill from 'ol/style/Fill';
import Stroke from 'ol/style/Stroke';
import Style from 'ol/style/Style';
import { map, withLatestFrom } from 'rxjs';
import { GenNighttimeLightOutageDays } from '../../../../generated/serverModels/GenNighttimeLightOutageDays';
import { AppService } from '../../../../services/app.service';
import { MapService } from '../../../../services/map.service';
import { selectCounties } from '../../../../state/location/location.reducer';
import { BaseUILayer } from '../../../classes/layers/base-ui-layer';
import {
  NighttimeLightsPopupComponent,
} from '../../../layer-popups/nighttime-lights-popup/nighttime-lights-popup.component';
import { LayerHeaderComponent } from '../../layer-header/layer-header.component';

@Component({
  selector: 'tgrid-nighttime-lights-layer',
  imports: [
    LayerHeaderComponent,
    ReactiveFormsModule,
    DecimalPipe,
  ],
  templateUrl: './nighttime-lights-layer.component.html',
  standalone: true,
  styleUrl: './nighttime-lights-layer.component.scss',
})
export class NighttimeLightsLayerComponent extends BaseUILayer<BaseLayer> {

  private appService = inject(AppService);
  private httpClient = inject(HttpClient);
  private store = inject(Store);

  protected legendBounds = signal({ min: 0, max: Number.MIN_SAFE_INTEGER });
  private legendScale = computed(() => {
    return scaleLinear([this.legendBounds().min, this.legendBounds().max], ['#e8cc65', '#c14a22']);
  });

  constructor() {
    super();
    effect(() => {
      this.appService.selectedStates();
      this.appService.selectedYears();
      if (this.checked) {
        this.getNighttimeLightsOutageDays();
      }
    });
  }

  override toggleLayer(checked: boolean): void {
    this.checked = checked;
    if (this.checked) {
      this.getNighttimeLightsOutageDays();
    } else {
      MapService.getMapRef()!.removeLayer(this.mapLayer().renderedLayer);
    }
  }

  override onDestroy(): void {
  }


  private getNighttimeLightsOutageDays(): void {
    const selectedYears = this.appService.selectedYears();

    if (selectedYears.length === 0) return;

    this.legendBounds.update(() => ({ min: 0, max: selectedYears.length * 365 }));

    let params = new HttpParams()
      .set('years', selectedYears.join());

    const states = this.appService.selectedStates().map(state => state.abbreviation);
    params = params.set('states', states.join());


    this.httpClient.get<GenNighttimeLightOutageDays[]>(this.mapLayer().serviceUrl, { params })
      .pipe(
        withLatestFrom(this.store.select(selectCounties)),
        map(([outages, counties]) => {
          return outages.map(outage => {
            return {
              type: 'Feature',
              properties: {
                ...outage,
                'popoverComponent': NighttimeLightsPopupComponent,
              },
              geometry: JSON.parse(counties.find(c => c.geoid === outage.fipsCode)!.boundary),
            };
          });
        }),
      )
      .subscribe(outages => {
        const features = new GeoJSON().readFeatures({ type: 'FeatureCollection', features: outages });

        if (this.mapLayer().renderedLayer && this.mapService.isLayerOnMap(this.mapLayer().renderedLayer)) {
          (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource()?.clear();
          (this.mapLayer().renderedLayer as VectorLayer<VectorSource>).getSource()?.addFeatures(features as Feature<Geometry>[]);
          return;
        }

        this.mapLayer().renderedLayer = new VectorLayer({
          source: new VectorSource({
            features: features as Feature<Geometry>[],
            attributions: this.mapLayer().buildAttribution(),
          }),
          properties: {
            name: this.mapLayer().displayName,
          },
          opacity: 0.8,
          style: (feature) => {
            const properties = feature.getProperties() as GenNighttimeLightOutageDays;
            return new Style({
              stroke: new Stroke({
                color: 'black',
                width: 1,
              }),
              fill: new Fill({
                color: this.legendScale()(properties.outageDays),
              }),
            });

          },
        });

        MapService.getMapRef()!.addLayer(this.mapLayer().renderedLayer);
      });

  }

}
